import React, { useState, useEffect } from 'react';
import { useAdmin } from '../../components/Admin/AdminContext';
import { 
  Database, Table, FileJson, ChevronRight, ChevronDown,
  Search, RefreshCw, Eye, Loader2, AlertCircle, 
  HardDrive, FileText, Hash, Clock, ArrowUpDown
} from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Badge } from '../../components/ui/badge';
import {
  Table as UITable,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../../components/ui/table';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function AdminDatabase() {
  const { adminToken } = useAdmin();
  const [databases, setDatabases] = useState([]);
  const [selectedDb, setSelectedDb] = useState(null);
  const [selectedCollection, setSelectedCollection] = useState(null);
  const [collections, setCollections] = useState([]);
  const [documents, setDocuments] = useState([]);
  const [schema, setSchema] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [expandedDbs, setExpandedDbs] = useState({});
  const [pagination, setPagination] = useState({ page: 1, limit: 20, total_count: 0 });
  const [stats, setStats] = useState(null);
  const [viewMode, setViewMode] = useState('table'); // 'table' or 'json'
  const [selectedDocument, setSelectedDocument] = useState(null);

  // Fetch databases on mount
  useEffect(() => {
    fetchDatabases();
    fetchGlobalStats();
  }, []);

  const fetchDatabases = async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await fetch(`${API_URL}/api/admin/db/databases`, {
        headers: { 'Authorization': `Bearer ${adminToken}` }
      });
      if (!response.ok) throw new Error('Erreur lors du chargement des bases');
      const data = await response.json();
      setDatabases(data.databases);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const fetchGlobalStats = async () => {
    try {
      const response = await fetch(`${API_URL}/api/admin/db/stats`, {
        headers: { 'Authorization': `Bearer ${adminToken}` }
      });
      if (response.ok) {
        const data = await response.json();
        setStats(data.summary);
      }
    } catch (err) {
      console.error('Error fetching stats:', err);
    }
  };

  const fetchCollections = async (dbName) => {
    setLoading(true);
    try {
      const response = await fetch(`${API_URL}/api/admin/db/databases/${dbName}/collections`, {
        headers: { 'Authorization': `Bearer ${adminToken}` }
      });
      if (!response.ok) throw new Error('Erreur lors du chargement des collections');
      const data = await response.json();
      setCollections(data.collections);
      setSelectedDb(dbName);
      setSelectedCollection(null);
      setDocuments([]);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const fetchDocuments = async (dbName, collName, page = 1) => {
    setLoading(true);
    try {
      const response = await fetch(
        `${API_URL}/api/admin/db/databases/${dbName}/collections/${collName}?page=${page}&limit=20`,
        { headers: { 'Authorization': `Bearer ${adminToken}` } }
      );
      if (!response.ok) throw new Error('Erreur lors du chargement des documents');
      const data = await response.json();
      setDocuments(data.documents);
      setPagination(data.pagination);
      setSelectedCollection(collName);
      
      // Also fetch schema
      fetchSchema(dbName, collName);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const fetchSchema = async (dbName, collName) => {
    try {
      const response = await fetch(
        `${API_URL}/api/admin/db/databases/${dbName}/collections/${collName}/schema`,
        { headers: { 'Authorization': `Bearer ${adminToken}` } }
      );
      if (response.ok) {
        const data = await response.json();
        setSchema(data.schema);
      }
    } catch (err) {
      console.error('Error fetching schema:', err);
    }
  };

  const toggleDbExpand = (dbName) => {
    setExpandedDbs(prev => ({
      ...prev,
      [dbName]: !prev[dbName]
    }));
    if (!expandedDbs[dbName]) {
      fetchCollections(dbName);
    }
  };

  const formatValue = (value) => {
    if (value === null || value === undefined) return <span className="text-gray-400">null</span>;
    if (typeof value === 'boolean') return <Badge variant={value ? "default" : "secondary"}>{value.toString()}</Badge>;
    if (typeof value === 'object') {
      if (Array.isArray(value)) return <span className="text-purple-600">[{value.length} items]</span>;
      return <span className="text-blue-600">{'{...}'}</span>;
    }
    if (typeof value === 'string' && value.length > 50) return value.substring(0, 50) + '...';
    return String(value);
  };

  const formatBytes = (bytes) => {
    if (bytes === 0) return '0 B';
    const k = 1024;
    const sizes = ['B', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  return (
    <div className="p-6 space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold flex items-center gap-2">
            <Database className="h-7 w-7 text-blue-600" />
            Explorateur de Base de Données
          </h1>
          <p className="text-gray-500 text-sm mt-1">
            Consultation en lecture seule de toutes les collections MongoDB
          </p>
        </div>
        <Button onClick={fetchDatabases} variant="outline" disabled={loading}>
          <RefreshCw className={`h-4 w-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
          Actualiser
        </Button>
      </div>

      {/* Global Stats */}
      {stats && (
        <div className="grid grid-cols-4 gap-4">
          <div className="bg-white p-4 rounded-lg border shadow-sm">
            <div className="flex items-center gap-2 text-gray-500 text-sm">
              <Database className="h-4 w-4" />
              Bases de données
            </div>
            <p className="text-2xl font-bold mt-1">{stats.total_databases}</p>
          </div>
          <div className="bg-white p-4 rounded-lg border shadow-sm">
            <div className="flex items-center gap-2 text-gray-500 text-sm">
              <Table className="h-4 w-4" />
              Collections
            </div>
            <p className="text-2xl font-bold mt-1">{stats.total_collections}</p>
          </div>
          <div className="bg-white p-4 rounded-lg border shadow-sm">
            <div className="flex items-center gap-2 text-gray-500 text-sm">
              <FileText className="h-4 w-4" />
              Documents
            </div>
            <p className="text-2xl font-bold mt-1">{stats.total_documents.toLocaleString()}</p>
          </div>
          <div className="bg-white p-4 rounded-lg border shadow-sm">
            <div className="flex items-center gap-2 text-gray-500 text-sm">
              <HardDrive className="h-4 w-4" />
              Taille totale
            </div>
            <p className="text-2xl font-bold mt-1">{stats.total_size_mb} MB</p>
          </div>
        </div>
      )}

      {/* Error Alert */}
      {error && (
        <div className="bg-red-50 border border-red-200 text-red-700 p-4 rounded-lg flex items-center gap-2">
          <AlertCircle className="h-5 w-5" />
          {error}
          <Button variant="ghost" size="sm" onClick={() => setError(null)} className="ml-auto">
            Fermer
          </Button>
        </div>
      )}

      <div className="flex gap-6">
        {/* Sidebar - Database Tree */}
        <div className="w-72 bg-white rounded-lg border shadow-sm overflow-hidden">
          <div className="p-3 bg-gray-50 border-b font-medium flex items-center gap-2">
            <Database className="h-4 w-4" />
            Bases de données
          </div>
          <div className="max-h-[600px] overflow-y-auto">
            {databases.map((db) => (
              <div key={db.name} className="border-b last:border-b-0">
                <button
                  onClick={() => toggleDbExpand(db.name)}
                  className={`w-full p-3 text-left hover:bg-gray-50 flex items-center gap-2 ${
                    selectedDb === db.name ? 'bg-blue-50' : ''
                  }`}
                >
                  {expandedDbs[db.name] ? (
                    <ChevronDown className="h-4 w-4 text-gray-400" />
                  ) : (
                    <ChevronRight className="h-4 w-4 text-gray-400" />
                  )}
                  <Database className="h-4 w-4 text-blue-600" />
                  <span className="font-medium text-sm">{db.name}</span>
                  <Badge variant="secondary" className="ml-auto text-xs">
                    {db.collections_count}
                  </Badge>
                </button>
                
                {expandedDbs[db.name] && (
                  <div className="bg-gray-50 pl-8 pr-2 py-1">
                    {db.collections.map((coll) => (
                      <button
                        key={coll}
                        onClick={() => fetchDocuments(db.name, coll)}
                        className={`w-full p-2 text-left text-sm hover:bg-white rounded flex items-center gap-2 ${
                          selectedCollection === coll ? 'bg-white font-medium text-blue-600' : 'text-gray-600'
                        }`}
                      >
                        <Table className="h-3 w-3" />
                        {coll}
                      </button>
                    ))}
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>

        {/* Main Content */}
        <div className="flex-1 bg-white rounded-lg border shadow-sm overflow-hidden">
          {selectedCollection ? (
            <>
              {/* Collection Header */}
              <div className="p-4 border-b bg-gray-50 flex items-center justify-between">
                <div>
                  <h2 className="font-semibold flex items-center gap-2">
                    <Table className="h-5 w-5 text-green-600" />
                    {selectedDb} / {selectedCollection}
                  </h2>
                  <p className="text-sm text-gray-500">
                    {pagination.total_count.toLocaleString()} documents
                  </p>
                </div>
                <div className="flex items-center gap-2">
                  <Button
                    variant={viewMode === 'table' ? 'default' : 'outline'}
                    size="sm"
                    onClick={() => setViewMode('table')}
                  >
                    <Table className="h-4 w-4 mr-1" />
                    Table
                  </Button>
                  <Button
                    variant={viewMode === 'json' ? 'default' : 'outline'}
                    size="sm"
                    onClick={() => setViewMode('json')}
                  >
                    <FileJson className="h-4 w-4 mr-1" />
                    JSON
                  </Button>
                </div>
              </div>

              {/* Schema Info */}
              {schema.length > 0 && (
                <div className="p-3 border-b bg-blue-50">
                  <p className="text-xs font-medium text-blue-700 mb-2">Champs détectés:</p>
                  <div className="flex flex-wrap gap-1">
                    {schema.slice(0, 10).map((field) => (
                      <Badge key={field.name} variant="outline" className="text-xs">
                        {field.name}
                        <span className="ml-1 text-gray-400">({field.types.join('/')})</span>
                      </Badge>
                    ))}
                    {schema.length > 10 && (
                      <Badge variant="secondary" className="text-xs">
                        +{schema.length - 10} autres
                      </Badge>
                    )}
                  </div>
                </div>
              )}

              {/* Documents */}
              <div className="overflow-auto max-h-[500px]">
                {loading ? (
                  <div className="p-8 text-center">
                    <Loader2 className="h-8 w-8 animate-spin mx-auto text-blue-600" />
                    <p className="mt-2 text-gray-500">Chargement...</p>
                  </div>
                ) : viewMode === 'table' ? (
                  <UITable>
                    <TableHeader>
                      <TableRow>
                        {schema.slice(0, 8).map((field) => (
                          <TableHead key={field.name} className="whitespace-nowrap">
                            {field.name}
                          </TableHead>
                        ))}
                        <TableHead>Actions</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {documents.map((doc, idx) => (
                        <TableRow key={doc._id || doc.id || idx}>
                          {schema.slice(0, 8).map((field) => (
                            <TableCell key={field.name} className="max-w-[200px] truncate">
                              {formatValue(doc[field.name])}
                            </TableCell>
                          ))}
                          <TableCell>
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => setSelectedDocument(doc)}
                            >
                              <Eye className="h-4 w-4" />
                            </Button>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </UITable>
                ) : (
                  <pre className="p-4 text-xs overflow-auto bg-gray-900 text-green-400">
                    {JSON.stringify(documents, null, 2)}
                  </pre>
                )}
              </div>

              {/* Pagination */}
              <div className="p-3 border-t bg-gray-50 flex items-center justify-between">
                <p className="text-sm text-gray-500">
                  Page {pagination.page} sur {pagination.total_pages}
                </p>
                <div className="flex gap-2">
                  <Button
                    variant="outline"
                    size="sm"
                    disabled={!pagination.has_prev}
                    onClick={() => fetchDocuments(selectedDb, selectedCollection, pagination.page - 1)}
                  >
                    Précédent
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    disabled={!pagination.has_next}
                    onClick={() => fetchDocuments(selectedDb, selectedCollection, pagination.page + 1)}
                  >
                    Suivant
                  </Button>
                </div>
              </div>
            </>
          ) : (
            <div className="p-8 text-center text-gray-500">
              <Database className="h-16 w-16 mx-auto text-gray-300 mb-4" />
              <p className="text-lg font-medium">Sélectionnez une collection</p>
              <p className="text-sm">Cliquez sur une base de données puis une collection pour voir les données</p>
            </div>
          )}
        </div>
      </div>

      {/* Document Detail Modal */}
      {selectedDocument && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50" onClick={() => setSelectedDocument(null)}>
          <div className="bg-white rounded-lg shadow-xl max-w-3xl w-full max-h-[80vh] overflow-hidden" onClick={e => e.stopPropagation()}>
            <div className="p-4 border-b flex items-center justify-between bg-gray-50">
              <h3 className="font-semibold flex items-center gap-2">
                <FileJson className="h-5 w-5 text-blue-600" />
                Détail du document
              </h3>
              <Button variant="ghost" size="sm" onClick={() => setSelectedDocument(null)}>
                ✕
              </Button>
            </div>
            <pre className="p-4 overflow-auto max-h-[60vh] text-sm bg-gray-900 text-green-400">
              {JSON.stringify(selectedDocument, null, 2)}
            </pre>
          </div>
        </div>
      )}
    </div>
  );
}
